import { Eye, Loader2 } from "lucide-react";
import React, { useCallback } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { SidebarMenuButton } from "../ui/sidebar";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { useSidebarItems } from "@/context/SideBarItemsProvider";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useProfilesQuery } from "@/hooks/useProfilesQuery";


export default function ProfileSelector() {
  const { isAdmin, isLoading } = useAdminStatus();
  const { activeProfile, setActiveProfile, profiles } = useSidebarItems();
  const {
    fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isError, refetch,
  } = useProfilesQuery();

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Load more when scrolled within 50px of the bottom
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    if (scrollBottom < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (!isAdmin) {
    return <></>;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="sm"
          className="h-8 text-xs text-sidebar-foreground/60 hover:text-sidebar-foreground data-[state=open]:bg-sidebar-accent"
          data-testid="profile-selector"
        >
          {isLoading || isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />}
          <span className="truncate">
            Viewing as <span className="font-medium">{isLoading || isPending ? "…" : activeProfile?.name}</span>
          </span>
          <CaretSortIcon className="ml-auto size-3.5" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onScroll={handleScroll}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 max-h-80 rounded-lg"
        align="start"
        side="top"
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          View the dashboard and shortcuts as
        </DropdownMenuLabel>
        {isPending && (
          <div className="my-2 flex h-16 items-center justify-center rounded border border-dashed">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}
        {isError && (
          <div className="my-2 flex h-16 flex-col items-center justify-center gap-1 rounded border border-dashed">
            <div className="text-center text-xs font-normal text-neutral-500">
              Could not load the groups
            </div>
            <button
              type="button"
              className="text-xs underline"
              onClick={(event) => {
                event.preventDefault();
                void refetch();
              }}
              data-testid="profile-selector-retry"
            >
              Try again
            </button>
          </div>
        )}
        {!isPending && !isError && profiles?.length === 0 && (
          <div className="my-2 flex h-16 flex-col items-center justify-center gap-2 rounded border border-dashed">
            <div className="text-center text-xs font-normal text-neutral-500">
              No profiles found
            </div>
          </div>
        )}
        {profiles?.map((profile) => {
          return (
            <DropdownMenuItem
              key={profile.id}
              disabled={isLoading}
              onClick={() => {
                setActiveProfile(profile);
              }}
              className="gap-2 p-2"
            >
              {profile.name}
            </DropdownMenuItem>
          );
        })}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
